import * as Yup from 'yup';
import React, { useEffect } from 'react';
import {
  Grid,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  Box,
} from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import FormTextField from 'hook-form-controls/FormTextField';
import FormCheckbox from 'hook-form-controls/FormCheckbox';
import { RoleSwitch } from 'core/Role/RoleSwitch';

export default function RoleInformationForm({ menus = [], menusLoading = false }) {
  const { control, setValue } = useFormContext();

  const menuValues = useWatch({
    control,
    name: menus.map((menu) => `menu_${menu.id}`),
  });

  const toggleAllMenus = (checked) => {
    menus.forEach((menu) => {
      setValue(`menu_${menu.id}`, checked, { menuchecked: true });
    });
  };

  const menuIdsKey = menus.map((m) => m.id).join(',');

  useEffect(() => {
    if (!menus.length) return;

    const allChecked = menus.every((menu, index) => menuValues?.[index] === true);

    setValue('enable_all', allChecked, { menuchecked: true });
  }, [menuValues, menuIdsKey, menus.length, setValue]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Typography mb={1} variant="h6">
          Role Name
        </Typography>
        <FormTextField
          name="roleName"
          label="Role Name"
          placeholder="Role name"
          fullWidth
          required
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography mb={1} variant="h6">
          Description
        </Typography>
        <FormTextField
          name="description"
          label="Description"
          placeholder="Short description"
          multiline
          fullWidth
        />
      </Grid>

      <Grid item xs={12}>
        <Typography mb={1}>Status</Typography>
        <Controller
          name="status"
          control={control}
          defaultValue="active"
          render={({ field }) => (
            <RadioGroup {...field} row>
              <FormControlLabel value="active" control={<Radio />} label="Active" />
              <FormControlLabel value="inactive" control={<Radio />} label="Inactive" />
            </RadioGroup>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={2}>
            <Typography fontWeight={500}>Select Menu Access:</Typography>
          </Grid>
          <Grid item xs={12} md={10}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Controller
                name="enable_all"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <RoleSwitch
                    {...field}
                    checked={field.value || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      field.onChange(checked);
                      toggleAllMenus(checked);
                    }}
                  />
                )}
              />
              <Typography variant="body2">Enable All</Typography>
            </Box>

            {menusLoading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading menus...</Typography>
              </Box>
            ) : (
              <Grid container spacing={1}>
                {menus.map((menu) => (
                  <Grid item xs={12} key={menu.id}>
                    <FormCheckbox name={`menu_${menu.id}`} label={menu.displayName} />
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

export const roleInformationSchema = Yup.object({
  roleName: Yup.string().required('Role Name is required'),
});
